import { useState, useEffect } from "react";
import "./index.css";
import { Weather } from "./components/Weather";
import { Notes } from "./components/Notes";
import { TodoList } from "./components/TodoList";
import { WeeklyPlan } from "./components/WeeklyPlan";
import { QROverlay } from "./components/QROverlay";
import { Joke } from "./components/Joke";
import { UnauthorizedPage } from "./UnauthorizedPage";
import { useAuthState, useQRCode, useBackgroundColor } from "./state";

export function App() {
  const auth = useAuthState();
  const qrCode = useQRCode();
  const backgroundColor = useBackgroundColor();
  const [registerUrl, setRegisterUrl] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);

  // Fetch register URL when QR code becomes visible (localhost only)
  useEffect(() => {
    if (qrCode.visible && auth.isLocalhost) {
      fetch("/api/auth/register-info")
        .then((res) => res.json())
        .then((data) => {
          const url = `${data.publicUrl}/register?token=${data.registerToken}`;
          setRegisterUrl(url);
        })
        .catch((err) => console.error("Failed to fetch register info:", err));
    } else if (!qrCode.visible) {
      setRegisterUrl(null);
    }
  }, [qrCode.visible, auth.isLocalhost]);

  // Show settings link on mouse/touch activity
  useEffect(() => {
    let hideTimeout: ReturnType<typeof setTimeout>;

    // Detect real smartphones via user agent
    const isMobile =
      /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );

    if (isMobile) {
      setShowSettings(true);
    }

    const handleInteraction = () => {
      setShowSettings(true);

      // On smartphones, show forever; on desktop, hide after 1 minute
      if (!isMobile) {
        clearTimeout(hideTimeout);
        hideTimeout = setTimeout(() => setShowSettings(false), 60000);
      }
    };

    window.addEventListener("mousemove", handleInteraction);
    window.addEventListener("touchstart", handleInteraction);

    return () => {
      window.removeEventListener("mousemove", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
      clearTimeout(hideTimeout);
    };
  }, []);

  // Show loading while checking auth
  if (!auth.checked) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-black/50 text-xl">Laden...</div>
      </div>
    );
  }

  // Show unauthorized page for non-localhost non-authorized users
  if (!auth.authorized && !auth.isLocalhost) {
    return <UnauthorizedPage />;
  }

  return (
    <>
      {/* QR Code Overlay - only shown on localhost when active */}
      {qrCode.visible && auth.isLocalhost && registerUrl && (
        <QROverlay
          registerUrl={registerUrl}
          remainingSeconds={qrCode.remainingSeconds}
        />
      )}

      {showSettings && (
        <a
          href="/edit"
          className="fixed bottom-4 right-4 bg-white/90 text-black/70 text-base font-medium px-4 py-3 rounded-full shadow-xl border border-black/10 z-50"
        >
          Einstellungen
        </a>
      )}
      <div
        className="w-screen md:h-screen grid grid-cols-1 md:overflow-x-hidden md:grid-cols-12 md:grid-rows-24 text-xl md:text-3xl text-black gap-4 pb-20 md:p-4"
        style={{ backgroundColor }}
      >
        <div className="col-span-1 md:col-span-7 md:row-span-24">
          <WeeklyPlan />
        </div>
        <div className="col-span-1 md:col-span-5 md:row-span-16">
          <TodoList />
        </div>
        <div className="col-span-1 md:col-span-5 md:row-span-4">
          <Weather />
        </div>
        <div className="col-span-1 md:col-span-5 md:row-span-4">
          <Joke />
        </div>
      </div>
    </>
  );
}

export default App;
